import { z } from 'zod';
// Login Validation Schema
export const loginSchema = z
  .object({
    loginType: z.enum(['username', 'phone']),

    username: z.string().optional(),

    phone: z.string().optional(),

    password: z.string().min(1, 'Please enter a password'),
  })
  .superRefine((data, ctx) => {
    // USERNAME LOGIN
    if (data.loginType === 'username') {
      if (!data.username?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['username'],
          message: 'Please Enter Username',
        });

        return;
      }

      if (data.username.length < 3) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['username'],
          message: 'Username must be at least 3 characters long',
        });
      }

      if (!/^[a-zA-Z0-9_]+$/.test(data.username)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['username'],
          message: 'Username can only contain letters, numbers, and underscores',
        });
      }
    }

    // PHONE LOGIN
    if (data.loginType === 'phone') {
      if (!data.phone?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['phone'],
          message: 'Please enter phone number',
        });
      }
    }
  });
export type LoginFormData = z.infer<typeof loginSchema>;
export const otpSchema = z.object({
  phone: z
    .string({
      required_error: 'Phone number is required',
      invalid_type_error: 'Phone number is required',
    })
    .nonempty('Phone number is required')
    .min(8, 'Phone number is too short')
    .max(15, 'Phone number is too long')
    .regex(
      /^\+[1-9]\d{1,14}$/,
      'Enter a valid phone number with country code (e.g. +8801711234567)'
    ),
});
export const signupSchema = otpSchema
  .extend({
    username: z
      .string()
      .trim()
      .min(1, 'Please Enter Username')
      .min(3, 'Username must be at least 3 characters long')
      .max(20, 'Username must not exceed 20 characters')
      .regex(/^[a-zA-Z0-9_]+$/, 'Username can only contain letters, numbers, and underscores'),

    password: z
      .string()
      .min(8, 'Password must be at least 8 characters long')
      .max(20, 'Password must not exceed 20 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/\d/, 'Password must contain at least one number')
      .regex(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character')
      .regex(/^\S*$/, 'Password must not contain spaces'),

    confirmPassword: z.string().min(1, 'Confirm Password is required'),

    referralCode: z
      .string()
      .max(15, 'Referral code must be at most 15 characters')
      .regex(/^[a-zA-Z0-9]*$/, 'Only alphanumeric characters are allowed')
      .optional()
      .or(z.literal('')),

    // comes from phone state
    dialCode: z.string().optional(),

    otp: z.string().length(6, 'OTP must be 6 digits').optional().or(z.literal('')),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type RegisterFormData = z.infer<typeof signupSchema>;
// Forgot Password Validation Schema
export const forgotPasswordSchema = otpSchema
  .extend({
    phone: z
      .string({
        required_error: 'Phone number is required',
        invalid_type_error: 'Phone number is required',
      })
      .nonempty('Phone number is required')
      .min(8, 'Phone number is too short')
      .max(15, 'Phone number is too long')
      .regex(
        /^\+[1-9]\d{1,14}$/,
        'Enter a valid phone number with country code (e.g. +8801711234567)'
      ),

    otp: z
      .string()
      .length(6, 'OTP must be exactly 6 digits')
      .regex(/^\d{6}$/, 'OTP must be a valid 6-digit number'),

    newPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters long')
      .max(20, 'Password must not exceed 20 characters')
      .regex(/^\S+$/, 'Password must not contain spaces')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/\d/, 'Password must contain at least one number')
      .regex(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character'),

    confirmPassword: z
      .string()
      .min(1, 'Confirm Password is required')
      .regex(/^\S+$/, 'Password must not contain spaces'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;
// Change Password Validation Schema
export const changePasswordSchema = z
  .object({
    oldPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters long')
      .max(20, 'Password must not exceed 20 characters')
      .regex(/^\S+$/, 'Password must not contain spaces'),

    newPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters long')
      .max(20, 'Password must not exceed 20 characters')
      .regex(/^\S+$/, 'Password must not contain spaces')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/\d/, 'Password must contain at least one number')
      .regex(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character'),

    confirmPassword: z
      .string()
      .min(1, 'Confirm Password is required')
      .regex(/^\S+$/, 'Password must not contain spaces'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })
  .refine((data) => data.oldPassword !== data.newPassword, {
    message: 'New password must be different from old password',
    path: ['newPassword'],
  }); // old & new password should differ

export type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;
